import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Send,
  X,
  ImagePlus,
  Bell,
  BellOff,
  BellRing,
  Lock,
} from "lucide-react";
import { toast } from "../ui";
import {
  discussionsApi,
  errMessage,
  topicWsUrl,
  SUB_MODE_META,
} from "./api";
import MessageBubble from "./MessageBubble";
import QuotedReplyChip from "./QuotedReplyChip";
import ModerationBar from "./ModerationBar";
import ReportDialog from "./ReportDialog";
import "./TopicThreadPage.css";

const POLL_ACTIVE_MS = 3000;
const POLL_IDLE_MS = 10000;

function uuid() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** Merge an incoming batch into the timeline, deduping by client_token then id
 *  (spec §11). Confirmed rows replace their optimistic placeholder; updates and
 *  tombstones patch in place. Returns a new, seq-sorted array. */
function mergeMessages(existing, incoming) {
  const byKey = new Map();
  const order = [];
  const put = (m) => {
    // Optimistic rows have no id yet; key them by client_token.
    const realId = m.id || null;
    const token = m.client_token || null;
    let target = null;
    if (token && byKey.has(`t:${token}`)) target = byKey.get(`t:${token}`);
    else if (realId && byKey.has(`i:${realId}`)) target = byKey.get(`i:${realId}`);
    if (target) {
      // Patch in place; prefer the confirmed (server) version. When a real
      // (server) row arrives it carries an id — clear the optimistic
      // _pending/_failed flags so the bubble confirms (server row never sets
      // them, and a plain spread would otherwise keep the stale true value).
      const merged = { ...target, ...m };
      if (realId) {
        merged._pending = false;
        merged._failed = false;
      }
      byKey.set(`t:${token}`, merged);
      if (realId) byKey.set(`i:${realId}`, merged);
      const idx = order.findIndex((o) => o === target);
      if (idx >= 0) order[idx] = merged;
      return;
    }
    if (token) byKey.set(`t:${token}`, m);
    if (realId) byKey.set(`i:${realId}`, m);
    order.push(m);
  };
  existing.forEach(put);
  incoming.forEach(put);
  // Dedup order (objects may be referenced under two keys).
  const seen = new Set();
  const unique = [];
  for (const m of order) {
    const k = m.id || `tok:${m.client_token}`;
    if (seen.has(k)) continue;
    seen.add(k);
    unique.push(m);
  }
  unique.sort((a, b) => {
    const sa = a.seq ?? Number.MAX_SAFE_INTEGER;
    const sb = b.seq ?? Number.MAX_SAFE_INTEGER;
    if (sa !== sb) return sa - sb;
    // Optimistic (no seq) rows fall back to created_at order.
    return new Date(a.created_at || 0) - new Date(b.created_at || 0);
  });
  return unique;
}

export default function TopicThreadPage({ isAdmin }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusMessageId = searchParams.get("m");

  const [topic, setTopic] = useState(null);
  const [topicError, setTopicError] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextBefore, setNextBefore] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const [composer, setComposer] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [wsLive, setWsLive] = useState(false);

  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const cursorsRef = useRef({ maxSeq: 0, maxUpdated: null });
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const reconnectRef = useRef(null);
  const focusedHandledRef = useRef(false);
  const currentUserId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}").id || null;
    } catch {
      return null;
    }
  }, []);

  // ---- cursor bookkeeping -------------------------------------------------
  const bumpCursors = useCallback((batch) => {
    const c = cursorsRef.current;
    batch.forEach((m) => {
      if (typeof m.seq === "number" && m.seq > c.maxSeq) c.maxSeq = m.seq;
      if (m.updated_at && (!c.maxUpdated || m.updated_at > c.maxUpdated)) {
        c.maxUpdated = m.updated_at;
      }
    });
  }, []);

  const isNearBottom = () => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  const scrollToBottom = (smooth) => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  };

  // Members the viewer has blocked. The server tombstones their messages on
  // the REST list path; this local set lets us tombstone messages arriving
  // LIVE over the socket and re-tombstone loaded ones the instant we block.
  const blockedIdsRef = useRef(new Set());
  useEffect(() => {
    discussionsApi
      .listBlocks()
      .then((r) => {
        blockedIdsRef.current = new Set(
          (r.blocks || []).map((b) => b.user && b.user.id).filter(Boolean)
        );
      })
      .catch(() => {});
  }, []);

  const applyBlockTombstone = useCallback((m) => {
    const aid = m.author && m.author.id;
    if (!aid || m.deleted || m.blocked || !blockedIdsRef.current.has(aid)) {
      return m;
    }
    return {
      ...m,
      blocked: true,
      body: null,
      has_media: false,
      media: [],
      mentions: [],
      reply_preview: null,
      like_count: 0,
      dislike_count: 0,
      my_reaction: null,
      author: { id: null, name: "Blocked member", avatar_url: null },
    };
  }, []);

  const applyBatch = useCallback(
    (batch, { keepScroll } = {}) => {
      if (!batch || batch.length === 0) return;
      const scrubbed = batch.map(applyBlockTombstone);
      const stick = isNearBottom();
      setMessages((prev) => mergeMessages(prev, scrubbed));
      bumpCursors(scrubbed);
      if (!keepScroll && stick) {
        requestAnimationFrame(() => scrollToBottom(true));
      }
    },
    [applyBlockTombstone, bumpCursors]
  );

  // ---- initial load -------------------------------------------------------
  const loadTopic = useCallback(() => {
    discussionsApi
      .getTopic(id)
      .then((t) => {
        setTopic(t);
        setTopicError("");
      })
      .catch((e) => setTopicError(errMessage(e)));
  }, [id]);

  useEffect(() => {
    loadTopic();
  }, [loadTopic]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setMessages([]);
    cursorsRef.current = { maxSeq: 0, maxUpdated: null };
    discussionsApi
      .listMessages(id, { limit: 30 })
      .then((d) => {
        if (!alive) return;
        const ordered = [...(d.items || [])].sort((a, b) => a.seq - b.seq);
        setMessages(ordered);
        bumpCursors(ordered);
        setNextBefore(d.next_before || null);
        setLoading(false);
        requestAnimationFrame(() => scrollToBottom(false));
      })
      .catch(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id, bumpCursors]);

  // ---- backfill (used on initial WS connect and reconnect) ---------------
  const backfill = useCallback(() => {
    return discussionsApi
      .listMessages(id, { after_seq: cursorsRef.current.maxSeq })
      .then((d) => applyBatch(d.items || []))
      .catch(() => {});
  }, [id, applyBatch]);

  // ---- short-poll fallback ------------------------------------------------
  const pollOnce = useCallback(async () => {
    const c = cursorsRef.current;
    try {
      const appendRes = await discussionsApi.listMessages(id, { after_seq: c.maxSeq });
      applyBatch(appendRes.items || []);
      if (c.maxUpdated) {
        const deltaRes = await discussionsApi.listMessages(id, {
          changed_since: c.maxUpdated,
          client_max_seq: c.maxSeq,
        });
        applyBatch(deltaRes.items || [], { keepScroll: true });
      }
    } catch {
      /* transient — next tick retries */
    }
  }, [id, applyBatch]);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    const tick = () => {
      pollOnce();
      const next = document.hasFocus() ? POLL_ACTIVE_MS : POLL_IDLE_MS;
      pollRef.current = setTimeout(tick, next);
    };
    pollRef.current = setTimeout(tick, POLL_ACTIVE_MS);
  }, [pollOnce]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // ---- WebSocket live channel (with short-poll fallback) -----------------
  useEffect(() => {
    if (loading) return undefined;
    let disposed = false;
    const token = localStorage.getItem("access_token");

    const connect = () => {
      if (disposed || !token) {
        startPolling();
        return;
      }
      let ws;
      try {
        ws = new WebSocket(topicWsUrl(id, token));
      } catch {
        startPolling();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        if (disposed) return;
        setWsLive(true);
        stopPolling();
        // Backfill any gap that opened before/while connecting.
        backfill();
      };
      ws.onmessage = (ev) => {
        if (disposed) return;
        try {
          const evt = JSON.parse(ev.data);
          if (evt && evt.message) {
            applyBatch([evt.message], { keepScroll: evt.type !== "message" });
          }
        } catch {
          /* ignore malformed frame */
        }
      };
      ws.onerror = () => {
        // Let onclose handle fallback/reconnect.
      };
      ws.onclose = () => {
        if (disposed) return;
        setWsLive(false);
        wsRef.current = null;
        // Fall back to short-poll immediately so nothing is missed, then try
        // to re-establish the live channel after a short backoff.
        startPolling();
        reconnectRef.current = setTimeout(() => {
          if (!disposed) connect();
        }, 5000);
      };
    };

    connect();

    return () => {
      disposed = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      stopPolling();
      const ws = wsRef.current;
      if (ws) {
        ws.onclose = null;
        ws.close();
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, loading]);

  // ---- mark read when messages arrive / on open --------------------------
  useEffect(() => {
    if (loading || messages.length === 0) return;
    const last = [...messages].reverse().find((m) => m.id && !m._pending);
    if (!last) return;
    const t = setTimeout(() => {
      discussionsApi.markRead(id, last.id).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [id, messages, loading]);

  // ---- scroll to / highlight ?m= target ----------------------------------
  useEffect(() => {
    if (loading || focusedHandledRef.current || !focusMessageId) return;
    if (messages.some((m) => m.id === focusMessageId)) {
      focusedHandledRef.current = true;
      jumpToMessage(focusMessageId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, focusMessageId, messages]);

  const jumpToMessage = (mid) => {
    const el = scrollRef.current?.querySelector(`[data-mid="${mid}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightId(mid);
      setTimeout(() => setHighlightId((h) => (h === mid ? null : h)), 1800);
    }
  };

  const loadMore = () => {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    const el = scrollRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    discussionsApi
      .listMessages(id, { before_seq: nextBefore })
      .then((d) => {
        const older = [...(d.items || [])].sort((a, b) => a.seq - b.seq);
        setMessages((prev) => mergeMessages(older, prev));
        setNextBefore(d.next_before || null);
        requestAnimationFrame(() => {
          if (el) el.scrollTop = el.scrollHeight - prevHeight;
        });
      })
      .catch((e) => toast.error(errMessage(e)))
      .finally(() => setLoadingMore(false));
  };

  // ---- send (optimistic) --------------------------------------------------
  const doSend = async (text, attached, token, optimisticRef) => {
    try {
      const created = await discussionsApi.sendMessage(id, {
        body: text || undefined,
        replyToMessageId: optimisticRef?.replyId || undefined,
        clientToken: token,
        files: attached,
      });
      applyBatch([created]);
    } catch (e) {
      // Mark the optimistic bubble failed (retry re-POSTs same token).
      setMessages((prev) =>
        prev.map((m) =>
          m.client_token === token && m._pending
            ? { ...m, _pending: false, _failed: true }
            : m
        )
      );
      toast.error(errMessage(e));
    }
  };

  const send = async () => {
    const text = composer.trim();
    if ((!text && files.length === 0) || sending) return;
    if (topic?.status === "CLOSED") {
      toast.error("This topic is closed.");
      return;
    }
    const token = uuid();
    const attached = files;
    const replyId = replyTo?.id || null;
    const optimistic = {
      client_token: token,
      seq: null,
      body: text || null,
      author: {
        id: currentUserId,
        name: JSON.parse(localStorage.getItem("user") || "{}").name || "You",
        avatar_url: JSON.parse(localStorage.getItem("user") || "{}").avatar_url || null,
      },
      media: [],
      mentions: [],
      reply_preview: replyTo
        ? {
            id: replyTo.id,
            author_name: replyTo.author?.name,
            body_excerpt: (replyTo.body || "").slice(0, 120),
            has_media: !!(replyTo.media && replyTo.media.length),
            deleted: false,
          }
        : null,
      created_at: new Date().toISOString(),
      _pending: true,
      _replyId: replyId,
    };
    setMessages((prev) => mergeMessages(prev, [optimistic]));
    setComposer("");
    setFiles([]);
    setReplyTo(null);
    requestAnimationFrame(() => scrollToBottom(true));
    setSending(true);
    await doSend(text, attached, token, { replyId });
    setSending(false);
  };

  const retry = async (msg) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.client_token === msg.client_token
          ? { ...m, _pending: true, _failed: false }
          : m
      )
    );
    await doSend(msg.body || "", [], msg.client_token, { replyId: msg._replyId });
  };

  // ---- message edit / delete ---------------------------------------------
  const editMessage = async (msg, newBody) => {
    try {
      const updated = await discussionsApi.editMessage(msg.id, newBody);
      applyBatch([updated], { keepScroll: true });
      return true;
    } catch (e) {
      toast.error(errMessage(e));
      return false;
    }
  };

  const deleteMessage = async (msg) => {
    if (msg._failed && !msg.id) {
      // Drop a failed optimistic bubble locally.
      setMessages((prev) => prev.filter((m) => m.client_token !== msg.client_token));
      return;
    }
    if (!window.confirm("Delete this message?")) return;
    try {
      const res = await discussionsApi.deleteMessage(msg.id);
      applyBatch([res], { keepScroll: true });
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const blockMember = async (msg) => {
    const aid = msg.author && msg.author.id;
    const name = (msg.author && msg.author.name) || "this member";
    if (!aid) return;
    if (
      !window.confirm(
        `Block ${name}? You'll stop seeing their messages in the community. ` +
          `They won't be told, and you can unblock them from "Blocked members" on the Discussions page.`
      )
    ) {
      return;
    }
    try {
      await discussionsApi.blockUser(aid);
      blockedIdsRef.current.add(aid);
      setMessages((prev) => prev.map(applyBlockTombstone));
      toast.success(`Blocked ${name}`);
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  // ---- subscription cycle -------------------------------------------------
  const cycleSub = async () => {
    if (!topic) return;
    const order = ["DEFAULT", "ALL", "MUTED"];
    const next = order[(order.indexOf(topic.my_sub_mode) + 1) % order.length];
    try {
      await discussionsApi.setSubscription(id, next);
      setTopic((t) => ({ ...t, my_sub_mode: next }));
      toast.success(SUB_MODE_META[next].hint);
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked].slice(0, 10));
    e.target.value = "";
  };

  if (topicError) {
    return (
      <div className="vdsc-thread-shell">
        <div className="vdsc-error">{topicError}</div>
        <Link to="/discussions" className="vdsc-back">
          <ArrowLeft size={15} /> Back to Discussions
        </Link>
      </div>
    );
  }

  const closed = topic?.status === "CLOSED";
  const SubIcon =
    topic?.my_sub_mode === "MUTED" ? BellOff : topic?.my_sub_mode === "ALL" ? BellRing : Bell;
  const canModerate = !!(isAdmin && topic?.can_moderate);

  return (
    <div className="vdsc-thread-shell">
      <header className="vdsc-thread-head">
        <button
          type="button"
          className="vdsc-back-btn"
          onClick={() => navigate("/discussions")}
          aria-label="Back to Discussions"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="vdsc-thread-titlewrap">
          <h1 className="vdsc-thread-title">
            {topic?.title || "Topic"}
            {closed && (
              <span className="vdsc-thread-closed">
                <Lock size={12} /> Closed
              </span>
            )}
          </h1>
          {topic?.description && (
            <p className="vdsc-thread-desc">{topic.description}</p>
          )}
        </div>
        <button
          type="button"
          className="vdsc-sub-btn"
          onClick={cycleSub}
          title={topic ? SUB_MODE_META[topic.my_sub_mode]?.hint : ""}
          aria-label="Notification setting"
        >
          <SubIcon size={16} />
          <span>{topic ? SUB_MODE_META[topic.my_sub_mode]?.label : ""}</span>
        </button>
      </header>

      {canModerate && (
        <ModerationBar
          topic={topic}
          onChanged={(t) => t && setTopic(t)}
          onDeleted={() => navigate("/discussions")}
        />
      )}

      <div className="vdsc-thread-body" ref={scrollRef}>
        {loading && <div className="vdsc-loading">Loading messages…</div>}

        {!loading && nextBefore && (
          <div className="vdsc-loadmore">
            <button type="button" className="vdsc-loadmore-btn" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? "Loading…" : "Load earlier messages"}
            </button>
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="vdsc-thread-empty">
            No messages yet. Be the first to say something.
          </div>
        )}

        {messages.map((m) => (
          <MessageBubble
            key={m.id || `tok:${m.client_token}`}
            message={m}
            mine={m.author?.id && m.author.id === currentUserId}
            canModerate={canModerate}
            highlighted={highlightId === m.id}
            onReply={(msg) => setReplyTo(msg)}
            onJumpToQuote={jumpToMessage}
            onEdit={editMessage}
            onDelete={deleteMessage}
            onReport={(msg) => setReportTarget(msg)}
            onBlock={blockMember}
            onRetry={retry}
          />
        ))}
      </div>

      {closed ? (
        <div className="vdsc-composer closed">
          <Lock size={15} /> This topic is closed — read only.
        </div>
      ) : (
        <div className="vdsc-composer">
          {replyTo && (
            <div className="vdsc-composer-reply">
              <QuotedReplyChip
                preview={{
                  id: replyTo.id,
                  author_name: replyTo.author?.name,
                  body_excerpt: (replyTo.body || "").slice(0, 120),
                  has_media: !!(replyTo.media && replyTo.media.length),
                  deleted: false,
                }}
              />
              <button
                type="button"
                className="vdsc-iconbtn"
                onClick={() => setReplyTo(null)}
                aria-label="Cancel reply"
              >
                <X size={15} />
              </button>
            </div>
          )}
          {files.length > 0 && (
            <div className="vdsc-composer-files">
              {files.map((f, i) => (
                <span key={i} className="vdsc-filechip">
                  {f.name}
                  <button
                    type="button"
                    onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                    aria-label={`Remove ${f.name}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="vdsc-composer-row">
            <button
              type="button"
              className="vdsc-iconbtn"
              onClick={() => fileInputRef.current?.click()}
              title="Attach images or a video"
              aria-label="Attach media"
            >
              <ImagePlus size={18} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              hidden
              onChange={onPickFiles}
            />
            <textarea
              className="vdsc-composer-input"
              placeholder="Write a message…"
              rows={1}
              value={composer}
              onChange={(e) => setComposer(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
            />
            <button
              type="button"
              className="vdsc-send-btn"
              onClick={send}
              disabled={sending || (!composer.trim() && files.length === 0)}
              aria-label="Send"
            >
              <Send size={18} />
            </button>
          </div>
          <div className="vdsc-composer-foot">
            <span className={`vdsc-live ${wsLive ? "on" : "off"}`}>
              {wsLive ? "Live" : "Live (polling)"}
            </span>
          </div>
        </div>
      )}

      <ReportDialog
        open={!!reportTarget}
        onOpenChange={(o) => !o && setReportTarget(null)}
        messageId={reportTarget?.id}
      />
    </div>
  );
}
